import { useAuth } from '../lib/auth-context';
import { getIntelligenceSources, type SourceCoverageRow } from '../lib/api';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { ApiErrorNotice } from '../components/ApiErrorNotice';
import { apiErrorFromResponse, shouldRetryApiError } from '../lib/api-errors';

// Coverage floor: warning letters only have posted-date coverage from 2026-01.
// Form 483 coverage floor is 2023-08.
const COVERAGE_FLOORS: Record<string, string> = {
  fda_warning_letters: '2026-01 (posted-date basis)',
  fda_form_483_citations: '2023-08',
};

function statusBadge(status: SourceCoverageRow['coverage_status']): React.ReactElement {
  const styles: Record<SourceCoverageRow['coverage_status'], string> = {
    active: 'bg-green-100 text-green-800',
    stale: 'bg-amber-100 text-amber-800',
    no_documents: 'bg-slate-100 text-slate-600',
  };
  const labels: Record<SourceCoverageRow['coverage_status'], string> = {
    active: 'Active',
    stale: 'Stale',
    no_documents: 'No documents',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

function freshnessLabel(lastIngestedAt: string | null): string {
  if (!lastIngestedAt) return '—';
  const ms = Date.now() - new Date(lastIngestedAt).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 8) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

export function IntelligenceSourcesPage(): React.ReactElement {
  const { user, session } = useAuth();

  const { data, error } = useSWR(
    user && session ? ['intelligence-sources', user.id] : null,
    async () => {
      const res = await getIntelligenceSources(session!.access_token);
      const body = (await res.json().catch(() => null)) as { sources?: SourceCoverageRow[]; error?: string } | null;
      if (!res.ok) {
        throw apiErrorFromResponse(res.status, body, 'Failed to load source coverage.');
      }
      return body?.sources ?? [];
    },
    { shouldRetryOnError: shouldRetryApiError },
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Intelligence: Sources</h2>
        <Link
          to="/intelligence/trends"
          className="text-sm font-medium text-sentinel-700 hover:text-sentinel-900"
        >
          View Trends →
        </Link>
      </div>

      <p className="text-sm text-slate-500 mb-6">
        Coverage table for every regulatory source in the registry. Sources with no documents are
        shown as gaps in the data — they are not hidden.
      </p>

      {error ? (
        <ApiErrorNotice error={error} fallbackMessage="Failed to load source coverage." />
      ) : !data ? (
        <div className="text-slate-500 text-sm">Loading source coverage…</div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Source</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Agency</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Status</th>
                  <th className="text-right px-4 py-3 text-slate-600 font-medium">Documents</th>
                  <th className="text-right px-4 py-3 text-slate-600 font-medium">Findings</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Earliest</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Latest</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Freshness</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr
                    key={row.source_id}
                    className={`border-t ${row.coverage_status === 'no_documents' ? 'bg-slate-50/60 text-slate-500' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <span className="font-medium text-slate-800">{row.source_family}</span>
                      {COVERAGE_FLOORS[row.source_id] && (
                        <span className="ml-2 text-xs text-slate-400">
                          floor: {COVERAGE_FLOORS[row.source_id]}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{row.agency}</td>
                    <td className="px-4 py-3">{statusBadge(row.coverage_status)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{row.document_count}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{row.finding_count}</td>
                    <td className="px-4 py-3 text-slate-500">{row.earliest_event_date ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-500">{row.latest_event_date ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-500">{freshnessLabel(row.last_document_ingested_at)}</td>
                  </tr>
                ))}
                {data.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                      No sources found. The source registry may not be populated yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-xs text-slate-400 space-y-1">
            <p>
              <span className="font-medium text-slate-500">Coverage floor disclosures:</span>{' '}
              FDA Warning Letters coverage begins 2026-01 (posted-date basis; issue dates may be earlier).
              FDA Form 483 citations coverage begins 2023-08.
            </p>
            <p>
              Sources with &quot;No documents&quot; status are gaps in the data — only enabled sources are
              actively ingested.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
